/**
 * ChatSlideOut — Wraps the unified Nabhos AI assistant in a slide-out panel.
 *
 * Answers both how-to questions (using help articles) and data questions
 * (using live mission analysis records fetched server-side). Sends
 * relevantContext (matched article text) with every message so the backend
 * can answer navigation/feature questions precisely.
 */

import { useState, useEffect, useRef } from 'react';
import { Send, Lightbulb, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SlideOutPanel from '../common/SlideOutPanel';
import { helpChatApi } from '../../services/api';
import { cn } from '../../lib/utils';
import { ARTICLES } from '../../pages/HelpPage';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ChatSlideOutProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChatSlideOut({ isOpen, onClose }: ChatSlideOutProps) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        id: '0',
        role: 'assistant',
        content:
          "Hi! 👋 I'm your Nabhos assistant. I can answer questions about how to use the platform AND questions about your actual mission data — crop diagnoses, anomalies, NDVI readings, and more. What would you like to know?",
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen]);

  // ── Article retrieval (same logic as HelpChat.tsx) ─────────────────────────

  const articleToText = (article: (typeof ARTICLES)[0]): string => {
    const lines: string[] = [`[${article.section}] ${article.title}`];
    for (const block of article.blocks) {
      if (block.text) lines.push(block.text);
      if (block.items) lines.push(...block.items.map((i) => `• ${i}`));
      if (block.headers && block.rows) {
        lines.push(block.headers.join(' | '));
        block.rows.forEach((row) => lines.push(row.join(' | ')));
      }
    }
    return lines.join('\n');
  };

  const getRelevantContext = (query: string, limit = 3): string => {
    const q = query.trim().toLowerCase();
    if (!q) return '';
    const words = q.split(/\s+/);
    const scored = ARTICLES.map((article) => {
      const corpus = [
        article.section,
        article.title,
        ...article.keywords,
        ...article.blocks.flatMap((b) => [
          b.text ?? '',
          ...(b.items ?? []),
          ...(b.rows ?? []).flat(),
        ]),
      ]
        .join(' ')
        .toLowerCase();
      const score = words.filter((w) => corpus.includes(w)).length;
      return { article, score };
    });
    const matches = scored
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(({ article }) => articleToText(article));
    return matches.join('\n\n---\n\n');
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessageContent = inputValue;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessageContent,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setSuggestedTopics([]);

    try {
      // Filter out welcome message (id='0') for API - Gemini requires first message to be from user
      const historyForApi = messages
        .filter((m) => m.id !== '0')
        .map((m) => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
        }));

      const relevantContext = getRelevantContext(userMessageContent);

      // Call help chat API
      const response = await helpChatApi.chat({
        message: userMessageContent,
        conversationHistory: historyForApi,
        relevantContext: relevantContext || undefined,
      });

      if (response.success) {
        // Check if service returned an error (e.g., quota exceeded)
        if (response.data?.error) {
          setError(response.data.error);
          return;
        }

        // Success case with response
        if (response.data?.response) {
          const assistantMessage: Message = {
            id: Date.now().toString(),
            role: 'assistant',
            content: response.data.response,
            timestamp: response.data.timestamp,
          };

          setMessages((prev) => [...prev, assistantMessage]);

          // Get suggested topics for follow-up
          try {
            const suggestionsResponse = await helpChatApi.getSuggestions(userMessage.content);
            if (
              suggestionsResponse.success &&
              Array.isArray(suggestionsResponse.data?.topics)
            ) {
              setSuggestedTopics(suggestionsResponse.data.topics);
            }
          } catch (err) {
            // Silently fail on suggestions
          }
        } else {
          setError('Failed to get response. Please try again.');
        }
      } else {
        setError('Failed to get response. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedTopic = (topic: string) => {
    setInputValue(topic);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const expandBtn = (
    <button
      onClick={() => { onClose(); navigate('/help?chat=open'); }}
      className="p-1.5 rounded-lg transition-colors text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-metallic-800"
      aria-label="Open in Help page"
      title="Open in Help page"
    >
      <Maximize2 className="w-5 h-5" />
    </button>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Ask Nabhos"
      width="medium"
      headerActions={expandBtn}
    >
      <div className="flex flex-col h-full">
        {/* Messages area */}
        <div className="flex-1 space-y-4 overflow-y-auto mb-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                'flex',
                message.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={cn(
                  'max-w-xs px-3 py-2.5 rounded-lg text-sm',
                  message.role === 'user'
                    ? 'bg-primary-500 text-white rounded-br-none'
                    : 'bg-gray-100 dark:bg-metallic-800 text-gray-900 dark:text-gray-100 rounded-bl-none'
                )}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
                <p className="text-xs mt-1 opacity-70">
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-metallic-800 px-3 py-2.5 rounded-lg">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0.2s' }}
                  />
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0.4s' }}
                  />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="flex justify-center">
              <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 px-3 py-2.5 rounded-lg text-sm max-w-xs">
                {error}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested topics */}
        {suggestedTopics.length > 0 && (
          <div className="p-3 bg-gray-50 dark:bg-metallic-800/50 border border-gray-200 dark:border-metallic-700 rounded-lg mb-4">
            <div className="flex items-start gap-2 mb-2">
              <Lightbulb className="w-3.5 h-3.5 text-primary-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                You might also ask about:
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestedTopics.map((topic, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestedTopic(topic)}
                  className="text-xs px-2.5 py-1 rounded-full bg-white dark:bg-metallic-700 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-metallic-600 transition-colors border border-primary-200 dark:border-primary-700"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="flex gap-2 border-t border-gray-200 dark:border-metallic-700 pt-3">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask your question..."
            disabled={isLoading}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-metallic-700 rounded-lg bg-white dark:bg-metallic-800 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSendMessage}
            disabled={isLoading || !inputValue.trim()}
            className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            title="Send message"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </SlideOutPanel>
  );
}

export default ChatSlideOut;

// Made with Bob
